import { useIntl } from 'react-intl'
import { useCallback, useContext } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import Pane from 'components/Common/Pane'
import { take } from 'lib/array/arrayUtils'
import Image from 'components/Common/Image'
import Heading from 'LuxKit/Typography/Heading'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import WishlistGuard from 'wishlists/components/WishlistGuard'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import OffsetBookmarkContainer from 'wishlists/components/Bookmark/Common/OffsetBookmarkContainer'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import GeoContext from 'contexts/geoContext'
import { connect } from 'react-redux'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import Group from 'components/utils/Group'
import { selectIsBedbankFlightBookingEnabled } from 'selectors/flightsSelectors'
import TextButton from 'LuxKit/Button/TextButton'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import TextLink from 'LuxKit/TextLink'
import BedbankOfferPriceDetails from 'components/OfferList/OfferCards/BedbankOfferPriceDetails'
import PriceRowFlightPackage from 'LuxKit/PricePoints/PriceRowFlightPackage'
import WalledContent from 'components/Common/WalledContent'
import OfferTileLoggedInButtons from './Support/OfferTileLoggedInButtons'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'

const Main = styled(Pane)`
  display: grid;
  position: relative;
  z-index: 0;
  grid-template-rows: ${rem(230)} auto;

  ${mediaQueryUp.tablet} {
    grid-template-rows: ${rem(360)} auto;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 67% 1fr;
    grid-template-rows: 1fr;
  }
`

const DetailContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.desktop} {
    flex: 1;
    padding: ${rem(16)};
  }
`

const DetailSegment = styled.div`
  position: relative;
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
  }

  ${mediaQueryUp.desktop} {
    flex: 1;
    text-align: center;
  }
`

const TopDetailSegment = styled.div`
  width: 100%;

  > * + * {
    margin-top: ${rem(12)};

    ${mediaQueryUp.desktop} {
      margin-top: ${rem(8)};
    }
  }
`

const OfferTitle = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  grid-column-gap: ${rem(16)};
  grid-auto-flow: column;

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr;
    grid-auto-flow: row;
    grid-row-gap: ${rem(8)};
    justify-items: center;
  }
`

const OfferTitleText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const OfferContent = styled.div`
  > * + * {
    margin-top: ${rem(8)};

    ${mediaQueryUp.desktop} {
      margin-top: ${rem(12)};
    }
  }
`

const MainImageSegment = styled.div`
  display: grid;
  gap: ${rem(4)};
  position: relative;

  ${mediaQueryUp.desktop} {
    &.additional {
      grid-template-rows: 1fr ${rem(120)};
    }
  }
`

const AdditionalImages = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    gap: ${rem(4)};
  }
`

const OfferOverlayContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(12)};
  inset-inline-start: ${rem(12)};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const USDesktopRating = styled(OfferRating)`
  justify-content: center;
`

const UrgencyLabelsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  grid-column: 1 / 1;

  ${mediaQueryUp.desktop} {
    justify-content: center;
  }

  > * + * {
    margin-inline-start: ${rem(4)};

    ${mediaQueryUp.desktop} {
      margin-inline-start: ${rem(12)};
    }
  }
`

const LogoWrapper = styled.div`
  grid-column-start: 2;
  grid-row: span 2;
  align-self: center;

  ${mediaQueryUp.desktop} {
    grid-row: auto;
    grid-column-start: auto;
  }
`

const Logo = styled(Image)`
  width: ${rem(90)};
  height: ${rem(60)};

  ${mediaQueryUp.desktop} {
    width: ${rem(150)};
    height: ${rem(100)};
  }
`

const BottomSegmentDetails = styled(Group)``
const BottomSegmentCTA = styled(Group)``
const BottomSegmentExtra = styled(Group)``

const BottomSegmentGrid = styled.div`
  display: grid;
  grid-column-gap: ${rem(8)};
  grid-template:
    "details cta"
    "extra extra" auto / 1fr ${rem(120)};

  > ${BottomSegmentDetails} {
    grid-area: details;
  }
  > ${BottomSegmentCTA} {
    grid-area: cta;
    align-self: end;
  }
  > ${BottomSegmentExtra} {
    grid-area: extra;
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    grid-template:
      "details"
      "." ${rem(16)}
      "cta"
      "extra" auto / 1fr;
  }
`

interface MappedProps {
  isBedbankFlightBookingEnabled: boolean
}

interface Props {
  offer: App.BedbankOffer
  eagerLoadFirstImage?: boolean
  filters?: App.OfferListFilters
  urgencyLabels?: React.ReactElement
}

const imageParams: ImageParams = {
  aspectRatio: '16:9',
  desktopWidth: '67vw',
  largeDesktopWidth: '750px',
  quality: 'good',
}

function FrontPageBedbankOfferTile(props: Props & MappedProps) {
  const {
    offer,
    eagerLoadFirstImage,
    urgencyLabels,
  } = props

  const {
    property,
    name,
  } = offer

  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const vendorLogoAlt = property.name
    ? intl.formatMessage({
        id: 'lt55KMjseodv7VeXsdo0',
        defaultMessage: '{propertyName} logo',
      }, { propertyName: property.name })
    : intl.formatMessage({
        id: 'Ej7vp6ZoV2Zk1u3itqiY',
        defaultMessage: 'Vendor logo',
      })

  const showAdditionalImages = offer.images.length > 3
  const location = [
    offer.property.address.city,
    offer.property.address.countryName,
  ]
    .filter(Boolean)
    .join(', ')

  const isUSRegion = currentRegionCode === 'US'
  const hasTravelUntil = !!offer.promotions[0]?.travelPeriod?.to
  const hasPricing = isDisplayablePrice(offer.sell?.price)

  const { currentRegionHasFlights } = useContext(GeoContext)
  const showFlightPricingDetails = !offer.walledGarden && currentRegionHasFlights && offer.flights.flightsEnabled

  const wall = <OfferTileLoggedInButtons
    align="none"
    fullWidth
  />

  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  return (
    <Main border="light-foreground" borderRadius="S">
      <MainImageSegment className={clsx({ additional: showAdditionalImages })}>
        <ImageCarousel
          images={offer.images}
          imageParams={imageParams}
          eagerLoadFirstImage={eagerLoadFirstImage}
          onImageLoad={handleImageLoaded}
        />
        {showAdditionalImages && <AdditionalImages>
          {take(offer.images, 3, 1).map((image) => (
            <Image
              key={image.id}
              id={image.id}
              alt={name}
              height={120}
              width={245}
              loading="lazy"
              fit="center"
            />
          ))}
        </AdditionalImages>}
        <OfferOverlayContainer>
          <ProductTypeLabel productType={offer.productType} />
        </OfferOverlayContainer>
      </MainImageSegment>
      <DetailContainer>
        <WishlistGuard>
          <CSSBreakpoint min="desktop">
            <Group direction="horizontal" horizontalAlign="space-between">
              {!isUSRegion && isOfferRatingDisplayable(offer.property?.rating) && <OfferRating
                variant="medium"
                rating={offer.property.rating}
              />}
              <BookmarkButton offer={offer} />
            </Group>
          </CSSBreakpoint>
        </WishlistGuard>
        <DetailSegment>
          <WishlistGuard>
            <CSSBreakpoint max="tablet">
              <OffsetBookmarkContainer>
                <BookmarkButton offer={offer} />
              </OffsetBookmarkContainer>
            </CSSBreakpoint>
          </WishlistGuard>
          <Group direction="vertical" gap={20} verticalAlign="space-between" fullHeight fullWidth>
            <TopDetailSegment>
              <OfferTitle>
                <LogoWrapper>
                  {offer.logoImageId && <Logo id={offer.logoImageId} width={300} aspectRatio="3:2" alt={vendorLogoAlt} />}
                </LogoWrapper>
                {urgencyLabels}
                <OfferTitleText>
                  <BodyText variant="large">{location}</BodyText>
                  <Heading variant="heading5">{property.name ?? offer.name}</Heading>
                </OfferTitleText>
              </OfferTitle>
              {isOfferRatingDisplayable(offer.property?.rating) && (
                <>
                  <CSSBreakpoint max="tablet">
                    <OfferRating
                      inlineLabel
                      variant="xs"
                      rating={offer.property.rating}
                    />
                  </CSSBreakpoint>
                  {isUSRegion && (
                    <CSSBreakpoint min="desktop">
                      <USDesktopRating
                        inlineLabel
                        variant="xs"
                        rating={offer.property.rating}
                      />
                    </CSSBreakpoint>
                  )}
                </>
              )}

              <OfferContent>
                <BodyText variant="large" lineClamp={3}>{offer.amenities}</BodyText>
                {hasTravelUntil && <TextLink size="medium" underline={false}>
                  {intl.formatMessage({
                    id: 'K2dwwPBasO-24XSIZ121',
                    defaultMessage: 'Travel until {date}',
                  }, { date: intlDateFormatter(offer.promotions[0].travelPeriod.to, INTL_DMY_CASUAL_FORMAT, intl.locale) })}
                </TextLink>}
                {!!offer.badge && <UrgencyLabelsWrapper>
                  <OfferBadgeLabel badge={offer.badge} />
                </UrgencyLabelsWrapper>}
              </OfferContent>
            </TopDetailSegment>
            <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing} wall={wall}>
              <BottomSegmentGrid>
                <BottomSegmentDetails direction="vertical" horizontalAlign="start" desktopHorizontalAlign="center" noDisplayWhenEmpty>
                  {hasPricing && <BedbankOfferPriceDetails offer={offer} horizontalAlign="center" />}
                </BottomSegmentDetails>
                <BottomSegmentCTA direction="vertical">
                  <TextButton kind="primary">
                    <CSSBreakpoint max="tablet">{intl.formatMessage({
                      id: 'i3cahYWcKeMNx673Qo-M',
                      defaultMessage: 'View',
                    })}
                    </CSSBreakpoint>
                    <CSSBreakpoint min="desktop">{intl.formatMessage({
                      id: '6PJ-eHYJjwGDEcCQZD7v',
                      defaultMessage: 'View Offer',
                    })}
                    </CSSBreakpoint>
                  </TextButton>
                </BottomSegmentCTA>
                <BottomSegmentExtra direction="vertical" horizontalAlign="start" desktopHorizontalAlign="center" noDisplayWhenEmpty>
                  {hasPricing && showFlightPricingDetails && <PriceRowFlightPackage price={undefined} product="hotel" />}
                </BottomSegmentExtra>
              </BottomSegmentGrid>
            </WalledContent>
          </Group>
        </DetailSegment>
      </DetailContainer>
    </Main>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  isBedbankFlightBookingEnabled: selectIsBedbankFlightBookingEnabled(state),
})

export default connect(mapStateToProps)(FrontPageBedbankOfferTile)
